import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from '../common/Loader.js';
import DateFilterDropdown from './DateFilterDropdown.js';
import analyticsService from '../../services/AnalyticsService';
import { AlertCircle, ArrowLeft, IndianRupee, Package } from 'lucide-react';
import Button from '../common/Button.js';
import { useTranslation } from 'react-i18next';
import {
  AnalyticsStatCard,
  AnalyticsTable,
  analyticsStatsGrid4Class,
  useAnalyticsCompanyScope,
  buildAnalyticsParams,
} from './common/index.js';
import { formatCurrencyPaise } from '../../utils/formatCurrency.js';
const formatCurrency = (v) => formatCurrencyPaise(v, '₹', { alwaysPaise: true });


const MissingReturnedAnalytics = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('current_month');
  const [customDates, setCustomDates] = useState({ from_date: '', to_date: '' });
  const {
    currentUser,
    companies,
    selectedCompany,
    handleCompanyChange,
    ready,
  } = useAnalyticsCompanyScope({ filter, customDates });

  useEffect(() => {
    if (ready) fetchData();
  }, [filter, selectedCompany, ready]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = buildAnalyticsParams({
        filter,
        customDates,
        selectedCompany,
        currentUser,
      });
      const response = await analyticsService.getMissingReturnedAnalytics(params);
      setData(response);
    } catch (error) {
      console.error('Error fetching missing/returned analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = AnalyticsStatCard;

  if (loading) {
    return (
      <DashboardLayout>
        <Loader fullScreen={false} size="default" message="Loading Missing & Returned Analytics..." />
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
              <div className="max-w-7xl mx-auto">
                {/* Header with Date Filter */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
                  <div>
                    <h1 className="text-2xl sm:text-xl font-semibold text-secondary">Missing & Returned Shipments</h1>
                    <p className="text-sm text-gray-600">Track and analyze shipment issues</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <DateFilterDropdown
                      filter={filter}
                      setFilter={setFilter}
                      customDates={customDates}
                      setCustomDates={setCustomDates}
                      onApply={fetchData}
                      dateRange={data?.filter}
                      currentUser={currentUser}
                      companies={companies}
                      selectedCompany={selectedCompany}
                      onCompanyChange={handleCompanyChange}
                      showCompanyFilter
                    />
                    <Button variant="primary" icon={ArrowLeft} onClick={() => navigate('/analytics')}>Back</Button>
                  </div>
                </div>

                {/* Summary Cards */}
                {data && (
                  <>
                    <div className={`${analyticsStatsGrid4Class} mb-6`}>
                      <StatCard
                        title="Total Missing"
                        value={data.summary.total_missing}
                        subtitle="Missing shipments"
                        icon={AlertCircle}
                        color="bg-rose-100 text-rose-600"
                      />
                      <StatCard
                        title="Total Returned"
                        value={data.summary.total_returned}
                        subtitle="Returned shipments"
                        icon={ArrowLeft}
                        color="bg-orange-100 text-orange-600"
                      />
                      <StatCard
                        title="Total Affected"
                        value={data.summary.total_affected}
                        subtitle="Combined count"
                        icon={Package}
                        color="bg-amber-100 text-amber-600"
                      />
                      <StatCard
                        title="Financial Impact"
                        value={formatCurrency(data.summary.total_financial_impact)}
                        subtitle="Total value"
                        icon={IndianRupee}
                        color="bg-purple-100 text-purple-600"
                      />
                    </div>

                    {/* Missing Shipments Section */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                      {/* Missing Shipments */}
                      <div className="bg-white rounded-xl shadow-md p-4 lg:p-6">
                        <div className="flex items-center mb-4">
                          <AlertCircle className="w-6 h-6 text-red-500 mr-2" />
                          <h2 className="text-lg font-semibold text-gray-800">{t('pages.missing.title')}</h2>
                        </div>

                        <div className="space-y-4">
                          {/* By Status */}
                          <div>
                            <h3 className="text-sm font-medium text-gray-700 mb-2">By Approval Status</h3>
                            <div className="space-y-2">
                              {Object.entries(data.missing_shipments.by_approval_status).map(([status, count]) => (
                                <div key={status} className="flex items-center justify-between p-2 bg-red-50 rounded">
                                  <span className="text-sm text-gray-700 capitalize">{status}</span>
                                  <span className="text-sm font-semibold text-red-600">{count}</span>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Financial Impact */}
                          <div className="border-t border-gray-200 pt-4">
                            <h3 className="text-sm font-medium text-gray-700 mb-2">Financial Impact</h3>
                            <div className="bg-gray-50 p-3 rounded">
                              <div className="flex justify-between mb-1">
                                <span className="text-sm text-gray-600">Count:</span>
                                <span className="text-sm font-semibold">{data.missing_shipments.financial_impact.count}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Value:</span>
                                <span className="text-sm font-semibold text-red-600">
                                  {formatCurrency(data.missing_shipments.financial_impact.total_value)}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Top Reasons */}
                          {data.missing_shipments.by_reason && data.missing_shipments.by_reason.length > 0 && (
                            <div className="border-t border-gray-200 pt-4">
                              <h3 className="text-sm font-medium text-gray-700 mb-2">Top Reasons</h3>
                              <div className="space-y-1">
                                {data.missing_shipments.by_reason.slice(0, 5).map((item, idx) => (
                                  <div key={idx} className="flex items-center justify-between text-xs">
                                    <span className="text-gray-600 truncate mr-2">{item.reason}</span>
                                    <span className="font-semibold text-gray-800">{item.count}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Returned Shipments */}
                      <div className="bg-white rounded-xl shadow-md p-4 lg:p-6">
                        <div className="flex items-center mb-4">
                          <ArrowLeft className="w-6 h-6 text-orange-500 mr-2" />
                          <h2 className="text-lg font-semibold text-gray-800">Returned Shipments</h2>
                        </div>

                        <div className="space-y-4">
                          {/* By Return Status */}
                          <div>
                            <h3 className="text-sm font-medium text-gray-700 mb-2">By Return Status</h3>
                            <div className="space-y-2">
                              {Object.entries(data.returned_shipments.by_return_status).map(([status, count]) => (
                                <div key={status} className="flex items-center justify-between p-2 bg-blue-50 rounded">
                                  <span className="text-sm text-gray-700 capitalize">{status}</span>
                                  <span className="text-sm font-semibold text-orange-600">{count}</span>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* By Approval Status */}
                          <div className="border-t border-gray-200 pt-4">
                            <h3 className="text-sm font-medium text-gray-700 mb-2">By Approval Status</h3>
                            <div className="space-y-2">
                              {Object.entries(data.returned_shipments.by_approval_status).map(([status, count]) => (
                                <div key={status} className="flex items-center justify-between p-2 bg-blue-50 rounded">
                                  <span className="text-sm text-gray-700 capitalize">{status}</span>
                                  <span className="text-sm font-semibold text-blue-600">{count}</span>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Financial Impact */}
                          <div className="border-t border-gray-200 pt-4">
                            <h3 className="text-sm font-medium text-gray-700 mb-2">Financial Impact</h3>
                            <div className="bg-gray-50 p-3 rounded space-y-1">
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Count:</span>
                                <span className="text-sm font-semibold">{data.returned_shipments.financial_impact.count}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Total Value:</span>
                                <span className="text-sm font-semibold text-gray-800">
                                  {formatCurrency(data.returned_shipments.financial_impact.total_value)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Refund Amount:</span>
                                <span className="text-sm font-semibold text-red-600">
                                  {formatCurrency(data.returned_shipments.financial_impact.refund_amount)}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Payment Workflow */}
                          <div className="border-t border-gray-200 pt-4">
                            <h3 className="text-sm font-medium text-gray-700 mb-2">Payment Workflow</h3>
                            <div className="space-y-2">
                              <div className="flex items-center justify-between p-2 bg-yellow-50 rounded">
                                <span className="text-sm text-gray-700">Payment Initiated</span>
                                <span className="text-sm font-semibold text-yellow-600">
                                  {data.returned_shipments.payment_workflow.payment_initiated}
                                </span>
                              </div>
                              <div className="flex items-center justify-between p-2 bg-green-50 rounded">
                                <span className="text-sm text-gray-700">Payment Refunded</span>
                                <span className="text-sm font-semibold text-green-600">
                                  {data.returned_shipments.payment_workflow.payment_refunded}
                                </span>
                              </div>
                              <div className="flex items-center justify-between p-2 bg-red-50 rounded">
                                <span className="text-sm text-gray-700">Pending Refund</span>
                                <span className="text-sm font-semibold text-red-600">
                                  {data.returned_shipments.payment_workflow.pending_refund}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Top Return Reasons */}
                    {data.returned_shipments.top_reasons && data.returned_shipments.top_reasons.length > 0 && (
                      <div className="bg-white rounded-xl shadow-md p-4 lg:p-6">
                        <h2 className="text-lg font-semibold text-gray-800 mb-4">Top Return Reasons</h2>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                          {data.returned_shipments.top_reasons.map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                              <span className="text-sm text-gray-700 flex-1 mr-3">{item.reason}</span>
                              <span className="text-sm font-semibold text-gray-800 bg-white px-2 py-1 rounded">{item.count}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
    </DashboardLayout>
  );
};

export default MissingReturnedAnalytics;